/**
 * Who reacted, as Discord shows it (2026-11-01):
 *
 * - hovering a reaction says "👍 reacted by Ali, Sara, Nima and 3 others";
 *   the names come from `GET .../reactions/{emoji}?limit=3` the first time
 *   the chip is hovered (or focused), and are kept for 30 seconds while the
 *   count stays the same, so moving over the chips again asks nobody;
 * - right-click or long-press a reaction, or the message menu's "View
 *   reactions", opens everyone who reacted, per emoji, 100 at a time (a
 *   dialog; a sheet from the bottom on a phone). Moderators (Manage
 *   Messages) can take a reaction off from there.
 *
 * The console's `reaction_details` flag turns both off (the server refuses
 * the route then too); the chips keep their counts.
 */
import {
  Permissions,
  tokenOfEmoji,
  type Message,
  type Reaction,
  type Snowflake,
  type User,
} from '@neosocial/protocol';
import { useEffect, useRef, useState, type MouseEvent } from 'react';
import { create } from 'zustand';
import { useT, type Translator } from '../i18n';
import { api } from '../lib/api';
import { useData, useDataShallow, type DataStore } from '../store/data';
import { toggleReaction } from '../store/messages';
import { can, channelPermissions, displayName } from '../store/selectors';
import { useFlag } from '../store/config';
import { Avatar, Btn, Icon } from '../ui/kit';
import { Dialog } from '../ui/overlay';
import { Emoji, emojiName } from './emoji';
import { openProfile } from './ProfileCard';
import { reportError } from './social';

/** How long a tooltip's names stay good (with the same count). */
export const REACTORS_TTL_MS = 30_000;
/** Names a tooltip shows before "and N others" (Discord). */
export const TIP_NAMES = 3;
/** One page of the full list. */
export const LIST_PAGE = 100;

export interface ReactorsEntry {
  users: User[];
  /** The reaction's count when these were fetched. */
  count: number;
  at: number;
}

const key = (messageId: Snowflake, emoji: string) => `${messageId} ${emoji}`;

/** The tooltips' names, by message and emoji. */
export const useReactors = create<{ entries: Record<string, ReactorsEntry> }>(() => ({
  entries: {},
}));
const inflight = new Map<string, Promise<void>>();

/** The names for this count, if they are fresh. */
export function freshReactors(
  messageId: Snowflake,
  emoji: string,
  count: number,
  now = Date.now(),
): ReactorsEntry | null {
  const e = useReactors.getState().entries[key(messageId, emoji)];
  return e && e.count === count && now - e.at < REACTORS_TTL_MS ? e : null;
}

/**
 * Asks for the first few people of a reaction, unless fresh names are
 * already here or the same question is on its way.
 */
export function ensureReactors(
  channelId: Snowflake,
  messageId: Snowflake,
  emoji: string,
  count: number,
): Promise<void> {
  if (freshReactors(messageId, emoji, count)) return Promise.resolve();
  const k = key(messageId, emoji);
  const going = inflight.get(k);
  if (going) return going;
  const p = api.channels
    .reactionUsers(channelId, messageId, emoji, { limit: TIP_NAMES })
    .then((users) => {
      useReactors.setState((s) => ({
        entries: { ...s.entries, [k]: { users, count, at: Date.now() } },
      }));
    })
    .finally(() => inflight.delete(k));
  inflight.set(k, p);
  return p;
}

/** Forgets every name (tests; a signed-out session). */
export function clearReactors(): void {
  inflight.clear();
  useReactors.setState({ entries: {} });
}

/** A name kept whole inside a sentence in the other direction (U+2068 … U+2069). */
const isolate = (s: string) => `⁨${s}⁩`;

/**
 * "👍 reacted by Ali", "… by Ali and Sara", "… by Ali, Sara and Nima",
 * "… by Ali, Sara, Nima and 3 others". `count` is the reaction's own count;
 * names beyond what the server sent count as others.
 */
export function reactionTip(
  t: Translator['t'],
  emoji: string,
  names: string[],
  count: number,
): string {
  const shown = names.slice(0, TIP_NAMES).map(isolate);
  if (!shown.length) return emoji;
  const others = Math.max(0, count - shown.length);
  let who: string;
  if (others > 0) who = t('rx.tip.others', { list: shown.join(', '), n: others });
  else if (shown.length === 1) who = shown[0]!;
  else who = t('rx.tip.and', { list: shown.slice(0, -1).join(', '), last: shown.at(-1)! });
  return t('rx.tip', { e: emoji, who });
}

/** The names a tooltip shows: each person as this place names them (nickname first). */
function useTipNames(entry: ReactorsEntry | null, guildId: Snowflake | undefined): string[] {
  return useDataShallow((d) =>
    entry
      ? entry.users
          .slice(0, TIP_NAMES)
          .map((u) =>
            displayName(d.users[u.id] ?? u, guildId ? d.members[guildId]?.[u.id] : undefined),
          )
      : NO_NAMES,
  );
}
const NO_NAMES: string[] = [];

/**
 * A reaction's token (what the chips, the store and the routes key on): a
 * custom emoji's message form `<:name:id>`, else the characters.
 */
const tokenOf = (r: Reaction) => tokenOfEmoji(r.emoji);
/** How a reaction reads in text: a custom emoji as `:name:` (Discord), else itself. */
export function reactionLabel(token: string): string {
  return token.startsWith('<') ? `:${emojiName(token) || '?'}:` : token;
}

/** A reaction's count: a new count rolls in (§7.14); the first one does not. */
function RxCount({ n, text }: { n: number; text: string }) {
  const [first] = useState(n);
  const rolled = first !== n;
  return (
    <span className={'rx-n nx-num' + (rolled ? ' is-rolling' : '')} key={rolled ? n : 'first'}>
      {text}
    </span>
  );
}

/**
 * One reaction chip. Its tooltip names who reacted once they are known
 * (the shared tooltip follows a changed `data-tip`, so the names replace
 * the emoji as soon as they arrive).
 */
export function ReactionChip({
  m,
  r,
  guildId,
  canJoin,
}: {
  m: Message;
  r: Reaction;
  guildId: Snowflake | undefined;
  canJoin: boolean;
}) {
  const { t, fmt } = useT();
  const e = tokenOf(r);
  const label = reactionLabel(e);
  const details = useFlag('reaction_details');
  const entry = useReactors((s) => {
    const x = s.entries[key(m.id, e)];
    return x && x.count === r.count ? x : null;
  });
  const names = useTipNames(entry, guildId);
  const ask = () => {
    if (details) void ensureReactors(m.channel_id, m.id, e, r.count).catch(() => {});
  };
  const tip = details && entry ? reactionTip(t, label, names, r.count) : label;
  return (
    <button
      type="button"
      className="rx-b"
      aria-pressed={r.me}
      aria-label={details && entry ? `${fmt.num(r.count)}, ${tip}` : `${label} ${fmt.num(r.count)}`}
      data-tip={tip}
      disabled={!canJoin && !r.me}
      onPointerEnter={(ev) => {
        if (ev.pointerType !== 'touch') ask();
      }}
      onFocus={ask}
      onClick={() => void toggleReaction(m, e).catch(reportError)}
      onContextMenu={(ev: MouseEvent) => {
        // right-click, or a long-press by touch (touch.ts sends contextmenu): everyone who reacted
        if (!details) return;
        ev.preventDefault();
        ev.stopPropagation();
        openReactionList(m.channel_id, m.id, e);
      }}
    >
      <Emoji e={e} cls="rx-e" />
      <RxCount n={r.count} text={fmt.num(r.count)} />
    </button>
  );
}

/* ------------------------------------------------------------ the full list */

interface ListOpen {
  channelId: Snowflake;
  messageId: Snowflake;
  emoji: string;
  n: number;
}
const useReactionList = create<{ open: ListOpen | null }>(() => ({ open: null }));

/** Opens everyone who reacted to a message, on `emoji`'s tab. */
export function openReactionList(channelId: Snowflake, messageId: Snowflake, emoji: string): void {
  useReactionList.setState((s) => ({
    open: { channelId, messageId, emoji, n: (s.open?.n ?? 0) + 1 },
  }));
}

export function closeReactionList(): void {
  useReactionList.setState({ open: null });
}

interface Page {
  users: User[];
  done: boolean;
  /** The reaction's count these were fetched for: another count asks again. */
  count: number;
  /** "Show more" is on its way. */
  more: boolean;
  error: boolean;
}

/** Mounted once (Shell): the "Reactions" dialog, a sheet on a phone. */
export function ReactionListHost() {
  const open = useReactionList((s) => s.open);
  const details = useFlag('reaction_details');
  if (!open || !details) return null;
  return <ReactionList key={open.n} open={open} />;
}

function ReactionList({ open }: { open: ListOpen }) {
  const { t, fmt } = useT();
  const m = useData((d) => d.messages[open.channelId]?.byId[open.messageId]);
  const guildId = useData((d) => d.channels[open.channelId]?.guild_id);
  const reactions = m?.reactions ?? NO_REACTIONS;
  const [emoji, setEmoji] = useState(open.emoji);
  const current = reactions.find((r) => tokenOf(r) === emoji) ?? reactions[0];
  const shown = current ? tokenOf(current) : '';
  const count = current?.count ?? 0;
  const [pages, setPages] = useState<Record<string, Page>>({});
  const got = pages[shown];
  // the tab's first page for this count is still on its way
  const loading = !got || got.count !== count;
  const users = got?.users ?? NO_USERS;
  // Manage Messages takes others' reactions off (the server checks it again)
  const manage = useData((d) => canManage(d, open.channelId));
  const tabs = useRef<HTMLDivElement>(null);

  // the message or its last reaction went away: nothing left to list
  useEffect(() => {
    if (!m || !reactions.length) closeReactionList();
  }, [m, reactions.length]);

  // the first page of the tab shown, again whenever its count changes
  useEffect(() => {
    if (!shown) return;
    let live = true;
    const done = (p: Partial<Page> & { users: User[] }) =>
      live &&
      setPages((all) => ({
        ...all,
        [shown]: { done: true, more: false, error: false, ...p, count },
      }));
    api.channels
      .reactionUsers(open.channelId, open.messageId, shown, { limit: LIST_PAGE })
      .then((list) => done({ users: list, done: list.length < LIST_PAGE }))
      .catch(() => done({ users: [], error: true }));
    return () => {
      live = false;
    };
  }, [open.channelId, open.messageId, shown, count]);

  const patch = (fn: (p: Page) => Page) =>
    setPages((all) => (all[shown] ? { ...all, [shown]: fn(all[shown]!) } : all));
  const more = () => {
    const last = users.at(-1);
    if (!last || loading || got?.more || got?.done) return;
    patch((p) => ({ ...p, more: true }));
    api.channels
      .reactionUsers(open.channelId, open.messageId, shown, { limit: LIST_PAGE, after: last.id })
      .then((list) =>
        patch((p) => {
          const seen = new Set(p.users.map((u) => u.id));
          return {
            ...p,
            users: [...p.users, ...list.filter((u) => !seen.has(u.id))],
            done: list.length < LIST_PAGE,
            more: false,
          };
        }),
      )
      .catch(() => patch((p) => ({ ...p, more: false, error: true })));
  };

  const remove = (u: User) =>
    void api.channels
      .removeUserReaction(open.channelId, open.messageId, shown, u.id)
      .then(() => patch((p) => ({ ...p, users: p.users.filter((x) => x.id !== u.id) })))
      .catch(reportError);

  return (
    <Dialog
      title={t('rx.list.title')}
      icon="smile"
      size="md"
      cls="rxl-dlg"
      onClose={closeReactionList}
    >
      <div className="rxl">
        <div
          className="rxl-tabs nx-scroll"
          role="tablist"
          aria-label={t('rx.list.tabs')}
          aria-orientation="vertical"
          ref={tabs}
          onKeyDown={(e) => {
            const i = reactions.findIndex((r) => tokenOf(r) === shown);
            const step =
              e.key === 'ArrowDown' || e.key === 'ArrowRight'
                ? 1
                : e.key === 'ArrowUp' || e.key === 'ArrowLeft'
                  ? -1
                  : 0;
            if (!step || i < 0) return;
            e.preventDefault();
            const next = reactions[(i + step + reactions.length) % reactions.length];
            if (!next) return;
            setEmoji(tokenOf(next));
            requestAnimationFrame(() =>
              tabs.current?.querySelector<HTMLElement>('[aria-selected="true"]')?.focus(),
            );
          }}
        >
          {reactions.map((r) => {
            const e = tokenOf(r);
            const on = e === shown;
            return (
              <button
                key={e}
                type="button"
                role="tab"
                className="rxl-tab"
                aria-selected={on}
                tabIndex={on ? 0 : -1}
                aria-label={`${reactionLabel(e)} ${fmt.num(r.count)}`}
                onClick={() => setEmoji(e)}
              >
                <Emoji e={e} cls="rx-e" />
                <span className="nx-num">{fmt.num(r.count)}</span>
              </button>
            );
          })}
        </div>
        <div className="rxl-list nx-scroll-read" role="tabpanel" aria-label={reactionLabel(shown)}>
          {got?.error && !users.length ? (
            <p className="ns-muted rxl-note" role="alert">
              {t('rx.list.error')}
            </p>
          ) : null}
          {loading && !users.length ? (
            <p className="ns-muted rxl-note">
              <i className="nx-spin" /> {t('ns.loading')}
            </p>
          ) : null}
          <ul>
            {users.map((u) => (
              <ReactorRow
                key={u.id}
                user={u}
                guildId={guildId}
                onRemove={manage ? () => remove(u) : undefined}
                removeLabel={t('rx.list.remove', { e: reactionLabel(shown) })}
              />
            ))}
          </ul>
          {users.length && got && !got.done ? (
            <div className="rxl-more">
              <Btn kind="ghost" size="sm" onClick={more} disabled={loading || got.more}>
                {t('rx.list.more')}
              </Btn>
            </div>
          ) : null}
        </div>
      </div>
    </Dialog>
  );
}
const NO_REACTIONS: Reaction[] = [];
const NO_USERS: User[] = [];

function canManage(d: DataStore, channelId: Snowflake): boolean {
  const ch = d.channels[channelId];
  return !!ch?.guild_id && can(channelPermissions(d, ch), Permissions.MANAGE_MESSAGES);
}

function ReactorRow({
  user,
  guildId,
  onRemove,
  removeLabel,
}: {
  user: User;
  guildId: Snowflake | undefined;
  onRemove?: () => void;
  removeLabel: string;
}) {
  const live = useData((d) => d.users[user.id] ?? user);
  const name = useData((d) =>
    displayName(live, guildId ? d.members[guildId]?.[user.id] : undefined),
  );
  return (
    <li className="rxl-row">
      <button
        type="button"
        className="rxl-who"
        aria-haspopup="dialog"
        onClick={(e) => openProfile(user.id, e.currentTarget)}
      >
        <Avatar user={live} name={name} size={32} />
        <span className="rxl-names">
          <b className="rxl-name" dir="auto">
            {name}
          </b>
          <small className="rxl-user" dir="ltr">
            {live.username}
          </small>
        </span>
      </button>
      {onRemove ? (
        <button
          type="button"
          className="rxl-rm"
          aria-label={`${removeLabel}: ${name}`}
          data-tip={removeLabel}
          onClick={onRemove}
        >
          <Icon name="x" size="s" />
        </button>
      ) : null}
    </li>
  );
}
